import { buildDateMeta } from '../../utils/dateUtils';
import { isTruthyEnrollmentEvidence } from '../../utils/enrollmentEvidence';

const TRACKED_PRODUCT_HISTORY_FIELDS = [
  'productSelectionStatus',
  'cancelDate',
  'billingEndDate',
  'billingStartDate',
  'effectiveDate',
  'goLiveDate',
  'productSelectionDate',
  'vendorProvidedDate',
  'referenceId',
  'isBilling',
];

const CANONICAL_PRODUCT_HISTORY_FIELD_LOOKUP = new Map(
  TRACKED_PRODUCT_HISTORY_FIELDS.map((field) => [field.toLowerCase(), field])
);
const CANONICAL_BAC_HISTORY_FIELD_LOOKUP = new Map(
  [
    ['enrollmentStatusCd', ['enrollmentStatusCd', 'programStatusCd', 'enrollmentStatus', 'programStatus']],
    ['apiPresenceStatus', ['apiPresenceStatus', 'apiPresence', 'presenceStatus']],
    ['programConsentStatusCd', ['programConsentStatusCd', 'programConsentStatus']],
    ['dataConsentStatusCd', ['dataConsentStatusCd', 'dataConsentStatus']],
    ['hasLiveSelections', ['hasLiveSelections']],
    ['isTerminated', ['isTerminated', 'isTerminatedFlag', 'terminatedFlag']],
    ['enrolledDt', ['enrolledDt', 'enrollmentDate', 'enrollmentDt']],
    ['terminationDate', ['terminationDate', 'isTerminatedDate', 'optOutDt', 'optOutDate']],
    ['lastUpdateDt', ['lastUpdateDt', 'lastUpdateDate', 'updateDate']],
    ['vendorStatus', ['vendorStatus']],
  ].flatMap(([canonical, aliases]) => aliases.map((alias) => [String(alias).toLowerCase(), canonical]))
);

const LEGACY_PRODUCT_FIELD_PATTERN = /^productSelections\[\d+\]\.([^.]+)$/i;
const LEGACY_VENDOR_FIELD_PATTERN = /^vendors\[\d+\]\.([^.]+)$/i;
const NULL_FILTERED_STATUS_FIELDS = new Set([
  'enrollmentStatusCd',
  'apiPresenceStatus',
  'programConsentStatusCd',
  'dataConsentStatusCd',
  'programSelectionStatus',
  'productSelectionStatus',
  'vendorStatus',
]);
const INITIAL_VISIBLE_BAC_STATUS_TOKENS = new Set([
  'PENDING_PROVIDER_CHANGE',
  'COOLING',
  'MISSING_FROM_API',
  'CANCELLED',
]);
const API_PRESENCE_STATUS_TOKENS = new Set(['PRESENT', 'COOLING', 'MISSING_FROM_API']);

const toCleanString = (value) => String(value || '').trim();
const toStatusToken = (value) =>
  String(value ?? '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .toUpperCase();

const canonicalizeLifecycleStatusToken = (value) => {
  const normalized = toStatusToken(value);
  return normalized === 'CANCELED' ? 'CANCELLED' : normalized;
};

const isBlankStatusValue = (value) => value === null || value === undefined || String(value).trim() === '';

/**
 * Keep only first-seen BAC lifecycle rows that the backend now treats as
 * intentional history signal. Ordinary null bootstrap rows should still stay
 * hidden in Dealer360 BAC history so operators do not see the March 28 clean
 * history backfill noise.
 */
const shouldKeepInitialBacLifecycleStatusRow = (field, previousValue, newValue) =>
  ((toCleanString(field) === 'enrollmentStatusCd' &&
    INITIAL_VISIBLE_BAC_STATUS_TOKENS.has(canonicalizeLifecycleStatusToken(newValue))) ||
    (toCleanString(field) === 'apiPresenceStatus' &&
      API_PRESENCE_STATUS_TOKENS.has(canonicalizeLifecycleStatusToken(newValue)))) &&
  isBlankStatusValue(previousValue) &&
  !isBlankStatusValue(newValue);

const shouldHideNullStatusChange = (field, previousValue, newValue) =>
  NULL_FILTERED_STATUS_FIELDS.has(toCleanString(field)) &&
  !shouldKeepInitialBacLifecycleStatusRow(field, previousValue, newValue) &&
  (isBlankStatusValue(previousValue) || isBlankStatusValue(newValue));

const deriveSelectionIdentity = (change = {}) => {
  const directReferenceId = toCleanString(change?.selectionReferenceId);
  const directProductCode = toCleanString(change?.selectionProductCode || change?.selectionProductBrandCode);
  if (directReferenceId || directProductCode) {
    return {
      selectionReferenceId: directReferenceId,
      selectionProductCode: directProductCode,
    };
  }

  const rawSelectionKey = toCleanString(change?.selectionKey);
  if (!rawSelectionKey) {
    return {
      selectionReferenceId: '',
      selectionProductCode: '',
    };
  }

  const parts = rawSelectionKey.includes('\u001f')
    ? rawSelectionKey.split('\u001f')
    : rawSelectionKey.split('::');

  return {
    selectionReferenceId: toCleanString(parts[parts.length - 1]),
    selectionProductCode: toCleanString(parts[3]),
  };
};

const normalizeVisibleProductHistoryField = (field) => {
  const normalizedField = toCleanString(field);
  if (!normalizedField) return null;

  const directMatch = CANONICAL_PRODUCT_HISTORY_FIELD_LOOKUP.get(normalizedField.toLowerCase());
  if (directMatch) return directMatch;

  const legacyMatch = normalizedField.match(LEGACY_PRODUCT_FIELD_PATTERN);
  if (!legacyMatch) return null;

  return CANONICAL_PRODUCT_HISTORY_FIELD_LOOKUP.get(toCleanString(legacyMatch[1]).toLowerCase()) || null;
};

const normalizeVisibleBacHistoryField = (field) => {
  const normalizedField = toCleanString(field);
  if (!normalizedField) return null;

  const directMatch = CANONICAL_BAC_HISTORY_FIELD_LOOKUP.get(normalizedField.toLowerCase());
  if (directMatch) return directMatch;

  const legacyVendorMatch = normalizedField.match(LEGACY_VENDOR_FIELD_PATTERN);
  if (legacyVendorMatch) {
    return CANONICAL_BAC_HISTORY_FIELD_LOOKUP.get(toCleanString(legacyVendorMatch[1]).toLowerCase()) || null;
  }

  return null;
};

const normalizeBacHistoryChange = (change = {}) => {
  const normalizedField = normalizeVisibleBacHistoryField(change?.field);
  if (!normalizedField) return null;

  const previousToken = canonicalizeLifecycleStatusToken(change?.previousValue);
  const newToken = canonicalizeLifecycleStatusToken(change?.newValue);
  const hasPresenceProvenance =
    [
      change?.apiPresenceDerived,
      change?.missingFromApi,
      change?.metadata?.apiPresenceDerived,
      change?.metadata?.missingFromApi,
    ].some(isTruthyEnrollmentEvidence) || change?.sourceField === 'apiPresenceStatus';
  const syntheticStateValue = [
    change?.syntheticCancellationState,
    change?.metadata?.syntheticCancellationState,
  ].find((value) => value !== null && value !== undefined && String(value).trim() !== '');
  const syntheticState = toStatusToken(syntheticStateValue);
  const hasSyntheticLifecycle = Boolean(
    isTruthyEnrollmentEvidence(change?.syntheticCancellation) ||
    isTruthyEnrollmentEvidence(change?.metadata?.syntheticCancellation) ||
    (syntheticState && !['FALSE', 'NO', 'N', '0'].includes(syntheticState))
  );
  const legacyPresenceProjection =
    hasPresenceProvenance &&
    !hasSyntheticLifecycle &&
    normalizedField === 'enrollmentStatusCd' &&
    API_PRESENCE_STATUS_TOKENS.has(newToken);
  const legacyPresenceRelease =
    hasPresenceProvenance &&
    !hasSyntheticLifecycle &&
    normalizedField === 'enrollmentStatusCd' &&
    API_PRESENCE_STATUS_TOKENS.has(previousToken) &&
    !API_PRESENCE_STATUS_TOKENS.has(newToken);

  return {
    ...change,
    field: legacyPresenceProjection ? 'apiPresenceStatus' : normalizedField,
    ...(legacyPresenceProjection && !API_PRESENCE_STATUS_TOKENS.has(previousToken)
      ? { previousValue: isBlankStatusValue(change?.previousValue) ? null : 'PRESENT' }
      : {}),
    ...(legacyPresenceRelease ? { previousValue: null } : {}),
    ...(legacyPresenceProjection
      ? { sourceField: normalizedField, legacyPresenceProjection: true }
      : legacyPresenceRelease
        ? { sourceField: normalizedField, legacyPresenceRelease: true }
        : {}),
  };
};

const withHistoryDisplayMeta = (change) => {
  const meta = buildDateMeta(change.changedAt || change.createdAt);
  return {
    id: change.id || buildHistoryRowId(change),
    changedAtDisplay: meta.display,
    changedAtSort: meta.sort,
    ...change,
  };
};

const buildHistoryRowId = (change = {}) =>
  [
    change.entityType || '',
    change.programName || '',
    change.field || '',
    change.changedAt || change.createdAt || '',
    change.selectionKey || '',
    change.selectionReferenceId || '',
    change.selectionProductCode || change.selectionProductBrandCode || '',
    change.selectionVendorCode || '',
    change.previousValue ?? '',
    change.newValue ?? '',
  ].join('::');

const getProductHistoryPrograms = (historyData = []) => {
  const unique = new Set(
    (historyData || [])
      .filter((change) => {
        if (change?.entityType && change.entityType !== 'product') return false;
        return Boolean(normalizeVisibleProductHistoryField(change?.field));
      })
      .map((change) => change.programName)
  );
  return ['All Programs', ...Array.from(unique).sort()];
};

const buildProductHistoryRows = ({
  historyData = [],
  productHistoryFilter = 'All Programs',
  productHistoryKind = 'All Changes',
  historySelectionFilter = null,
}) => {
  let filtered = (historyData || [])
    .map((change) => {
      if (change?.entityType && change.entityType !== 'product') return null;
      const normalizedField = normalizeVisibleProductHistoryField(change?.field);
      if (!normalizedField) return null;
      return {
        ...change,
        field: normalizedField,
        ...deriveSelectionIdentity(change),
      };
    })
    .filter((change) => !shouldHideNullStatusChange(change?.field, change?.previousValue, change?.newValue))
    .filter(Boolean);

  filtered = productHistoryFilter === 'All Programs'
    ? filtered
    : filtered.filter((change) => change.programName === productHistoryFilter);

  if (productHistoryKind === 'Selection Status Changes') {
    filtered = filtered.filter((change) => String(change?.field || '').toLowerCase() === 'productselectionstatus');
  }

  if (historySelectionFilter) {
    filtered = filtered.filter((change) => {
      if (historySelectionFilter.programName && change.programName !== historySelectionFilter.programName) return false;
      if (
        historySelectionFilter.selectionReferenceId &&
        toCleanString(change.selectionReferenceId) !== historySelectionFilter.selectionReferenceId
      ) {
        return false;
      }
      if (
        historySelectionFilter.selectionProductCode &&
        toCleanString(change.selectionProductCode) !== historySelectionFilter.selectionProductCode
      ) {
        return false;
      }
      return true;
    });
  }

  return filtered.map(withHistoryDisplayMeta);
};

const buildProductHistoryCounts = (productHistoryRows = []) => ({
  total: productHistoryRows.length,
  selectionStatus: productHistoryRows.filter((change) => String(change?.field || '').toLowerCase() === 'productselectionstatus').length,
});

const getBacHistoryPrograms = (historyData = []) => {
  const unique = new Set(
    (historyData || [])
      .filter((change) => {
        if (change?.entityType) {
          return ['bac', 'vendor'].includes(change.entityType) && Boolean(normalizeVisibleBacHistoryField(change?.field));
        }
        return Boolean(normalizeVisibleBacHistoryField(change?.field));
      })
      .map((change) => change.programName)
  );
  return ['All Programs', ...Array.from(unique).sort()];
};

const buildBacHistoryRows = ({ historyData = [], bacHistoryFilter = 'All Programs' }) => {
  let filtered = (historyData || [])
    .map((change) => {
      if (change?.entityType && !['bac', 'vendor'].includes(change.entityType)) return null;
      return normalizeBacHistoryChange(change);
    })
    .filter((change) => !shouldHideNullStatusChange(change?.field, change?.previousValue, change?.newValue))
    .filter(Boolean);

  filtered = bacHistoryFilter === 'All Programs'
    ? filtered
    : filtered.filter((change) => change.programName === bacHistoryFilter);

  return filtered.map(withHistoryDisplayMeta);
};

const buildBacHistoryCounts = (bacHistoryRows = []) => ({
  total: bacHistoryRows.length,
  vendorStatus: bacHistoryRows.filter((change) => String(change?.field || '').toLowerCase() === 'vendorstatus').length,
  apiPresence: bacHistoryRows.filter((change) => String(change?.field || '').toLowerCase() === 'apipresencestatus').length,
});

export {
  buildBacHistoryCounts,
  buildBacHistoryRows,
  buildHistoryRowId,
  buildProductHistoryCounts,
  buildProductHistoryRows,
  deriveSelectionIdentity,
  getBacHistoryPrograms,
  getProductHistoryPrograms,
  normalizeVisibleBacHistoryField,
  normalizeVisibleProductHistoryField,
};
